import type { Message } from '@/shared/api/websocket/types'

import { useEffect, useEffectEvent } from 'react'
import { useLocation } from '@tanstack/react-router'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'

import { Button } from '@/shared/components/ui/button'
import { queryClient } from '@/shared/lib/query-client'
import { useSocketChannel } from '@/shared/lib/socket/provider'

type OptimizationUpdateEventPayload = {
	optimization_id: string
	optimization_group_id: string
	optimization_status: string
	action?: string
	run_type: 'default' | 'dry_run'
	iteration_id?: string
}
const TOAST_ID = 'global-assortment-update'
export const useOptimizationUpdateChannel = () => {
	const { t } = useTranslation()
	const location = useLocation()
	const channel = useSocketChannel('optimization')

	useEffect(() => {
		toast.dismiss(TOAST_ID)
	}, [location.href])

	const handler = useEffectEvent(
		(msg: Message<OptimizationUpdateEventPayload>) => {
			toast.info(
				t('notification.page_updated.title', {
					defaultValue: 'The data was updated.',
				}),
				{
					description: t('notification.page_updated.description', {
						defaultValue: 'Please refresh the page to view the fresh data.',
					}),
					id: TOAST_ID,
					action: (
						<Button
							size='sm'
							onClick={() => {
								resetCommonQueries()
								toast.dismiss(TOAST_ID)
							}}
						>
							{t('general_refresh', { defaultValue: 'Refresh' })}
						</Button>
					),
					duration: Infinity,
				}
			)
		}
	)
	useEffect(() => {
		if (channel) {
			channel.subscribe()
			channel.on('update', handler)
		}

		return () => {
			toast.dismiss(TOAST_ID)
			channel?.off('update', handler)
		}
	}, [channel, t])
}

//Reset common queries
const resetCommonQueries = () => {
	queryClient.invalidateQueries({
		queryKey: ['optimizations'],
	})
	queryClient.invalidateQueries({
		queryKey: ['optimization-groups'],
	})
	queryClient.invalidateQueries({
		queryKey: ['pricing-alerts'],
	})
	queryClient.invalidateQueries({
		queryKey: ['assortment-list'],
	})
}
